import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────
interface AttendanceBarcodeModalProps {
  open: boolean;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
  .bm-root * { box-sizing:border-box; font-family:'DM Sans',sans-serif; }

  @keyframes bm-spin   { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes bm-fadeIn { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }
  @keyframes bm-scan   { 0%{top:0} 100%{top:calc(100% - 3px)} }
  @keyframes bm-pulse  { 0%,100%{opacity:1} 50%{opacity:.45} }
  @keyframes bm-glow   { 0%,100%{box-shadow:0 0 0 0 rgba(59,130,246,.4)} 50%{box-shadow:0 0 0 10px rgba(59,130,246,0)} }

  .bm-qr-wrap {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    animation: bm-fadeIn .3s ease;
    box-shadow: 0 8px 32px rgba(30,58,138,.18);
  }
  .bm-scan-line {
    position: absolute; left:0; right:0; height:3px;
    background: linear-gradient(90deg, transparent, #3b82f6, transparent);
    animation: bm-scan 2s ease-in-out infinite alternate;
    pointer-events: none; z-index: 10;
    box-shadow: 0 0 12px 4px rgba(59,130,246,.5);
  }

  .bm-countdown-ring {
    position: relative;
    width: 72px; height: 72px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
  }
  .bm-countdown-ring svg { position:absolute; top:0; left:0; transform:rotate(-90deg); }

  .bm-live-dot {
    width:8px; height:8px; border-radius:50%; background:#22c55e;
    animation: bm-pulse 1.5s ease-in-out infinite;
    flex-shrink:0;
  }
  .bm-live-dot--error { background:#ef4444; }

  /* QR corner decorations */
  .bm-corner {
    position:absolute; width:22px; height:22px;
    border-color: rgba(59,130,246,.7);
  }
  .bm-corner--tl { top:-1px; left:-1px;  border-top:3px solid; border-left:3px solid;  border-radius:5px 0 0 0; }
  .bm-corner--tr { top:-1px; right:-1px; border-top:3px solid; border-right:3px solid; border-radius:0 5px 0 0; }
  .bm-corner--bl { bottom:-1px; left:-1px;  border-bottom:3px solid; border-left:3px solid;  border-radius:0 0 0 5px; }
  .bm-corner--br { bottom:-1px; right:-1px; border-bottom:3px solid; border-right:3px solid; border-radius:0 0 5px 0; }

  /* Override PrimeReact Dialog */
  .bm-dialog .p-dialog { border-radius:24px!important; overflow:hidden!important; box-shadow:0 25px 60px rgba(0,0,0,.22)!important; }
  .bm-dialog .p-dialog-header { display:none!important; }
  .bm-dialog .p-dialog-content { padding:0!important; border-radius:24px!important; }
  .bm-dialog .p-dialog-mask { backdrop-filter:blur(6px)!important; background:rgba(0,0,0,.35)!important; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// QR renderer using canvas (no external library needed)
// We'll use a simple API-based QR image URL, or if you have react-native-qrcode-svg
// equivalent for web, use qrcode.react
// ─────────────────────────────────────────────────────────────────────────────

// Use qrcode.react if installed: npm install qrcode.react
// import { QRCodeSVG } from 'qrcode.react';
// If not installed, we'll render the token as text + show a placeholder.
// The import below is conditional — replace with your actual QR library.

let QRCodeSVG: React.FC<{ value: string; size: number; bgColor?: string; fgColor?: string; level?: string }> | null = null;
try {
  // Dynamic import fallback
  const mod = require('qrcode.react');
  QRCodeSVG = mod.QRCodeSVG ?? mod.default;
} catch {
  QRCodeSVG = null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Modal
// ─────────────────────────────────────────────────────────────────────────────
const AttendanceBarcodeModal: React.FC<AttendanceBarcodeModalProps> = ({ open, onClose }) => {
  const [token,       setToken]       = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [session,     setSession]     = useState(0);
  const [rotating,    setRotating]    = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState('');

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fadeRef     = useRef(false);

  const fetchToken = useCallback(async (isRotation = false) => {
    try {
      if (isRotation) setRotating(true);
      const res = await apiClient.get('/api/staff-attendance/barcode');
      if (res.data.status === 'success') {
        setToken(res.data.token);
        setSecondsLeft(res.data.expires_in ?? 30);
        setGeneratedAt(res.data.generated_at ?? '');
        if (isRotation) setSession(s => s + 1);
        setError(null);
      } else {
        setError(res.data.message ?? 'Failed to fetch QR');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Network error');
    } finally {
      if (isRotation) setRotating(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setToken(null); setSecondsLeft(30); setSession(0); setError(null);
      return;
    }
    fetchToken(false);
    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) { fetchToken(true); return 30; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [open, fetchToken]);

  const pct         = secondsLeft / 30;
  const isWarning   = secondsLeft <= 7;
  const ringColor   = isWarning ? '#f59e0b' : '#22c55e';
  const circumference = 2 * Math.PI * 30;
  const dash        = circumference * pct;

  return (
    <>
      <style>{CSS}</style>
      <Dialog
        visible={open}
        onHide={onClose}
        className="bm-dialog"
        style={{ width: '92vw', maxWidth: 480 }}
        modal draggable={false} resizable={false} blockScroll
      >
        <div className="bm-root" style={{ background: '#f8fafc', borderRadius: 24, overflow: 'hidden' }}>

          {/* ── Header gradient ── */}
          <div style={{
            background: 'linear-gradient(135deg,#1e3a8a 0%,#2855ae 55%,#3b82f6 100%)',
            padding: '20px 24px 18px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(255,255,255,.15)',
                  border: '1px solid rgba(255,255,255,.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                    <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="3" height="3"/>
                    <rect x="18" y="14" width="3" height="3"/><rect x="14" y="18" width="3" height="3"/>
                    <rect x="18" y="18" width="3" height="3"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: 'white' }}>Attendance QR Code</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', fontWeight: 500 }}>
                    SKOOLPILLAR · Staff Attendance
                  </div>
                </div>
              </div>
              <button onClick={onClose} style={{
                width: 32, height: 32, borderRadius: 9,
                background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.25)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 16, lineHeight: 1,
              }}>×</button>
            </div>

            {/* Session + live indicator */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div className={`bm-live-dot${error ? ' bm-live-dot--error' : ''}`} />
                <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,.8)' }}>
                  {error ? 'ERROR' : 'LIVE'} · Session #{session + 1}
                </span>
              </div>
              {generatedAt && (
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', fontFamily: 'monospace' }}>
                  {generatedAt.substring(11, 19)}
                </span>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>

            {/* Countdown ring + QR side by side */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, width: '100%' }}>

              {/* Countdown ring */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <div className="bm-countdown-ring">
                  <svg width="72" height="72" viewBox="0 0 72 72">
                    <circle cx="36" cy="36" r="30" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                    <circle cx="36" cy="36" r="30" fill="none"
                      stroke={ringColor} strokeWidth="6"
                      strokeDasharray={`${dash} ${circumference}`}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dasharray .9s linear, stroke .3s' }}
                    />
                  </svg>
                  <div style={{ fontSize: 20, fontWeight: 800, color: ringColor, lineHeight: 1 }}>{secondsLeft}</div>
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  seconds
                </div>
                {isWarning && (
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: '#f59e0b',
                    background: 'rgba(245,158,11,.1)', padding: '2px 8px', borderRadius: 6,
                    border: '1px solid rgba(245,158,11,.2)',
                  }}>Rotating soon</div>
                )}
              </div>

              {/* QR code area */}
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                {error ? (
                  <div style={{
                    width: '100%', minHeight: 200, borderRadius: 16,
                    background: 'rgba(239,68,68,.04)', border: '1.5px dashed rgba(239,68,68,.3)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                  }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01"/>
                    </svg>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626', textAlign: 'center', maxWidth: 180 }}>{error}</div>
                    <button onClick={() => fetchToken(false)} style={{
                      padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,.3)',
                      background: 'rgba(239,68,68,.08)', color: '#dc2626',
                      fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif',
                    }}>Retry</button>
                  </div>
                ) : !token ? (
                  <div style={{
                    width: '100%', minHeight: 200, borderRadius: 16,
                    background: 'linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%)',
                    backgroundSize: '600px 100%',
                    animation: 'bm-shimmer 1.3s infinite linear',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                      style={{ animation: 'bm-spin 1s linear infinite' }}>
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                  </div>
                ) : (
                  <div className="bm-qr-wrap" style={{
                    opacity: rotating ? 0.4 : 1,
                    transition: 'opacity .2s',
                    background: 'white', padding: 12,
                  }}>
                    {/* Corner decorations */}
                    <div className="bm-corner bm-corner--tl" />
                    <div className="bm-corner bm-corner--tr" />
                    <div className="bm-corner bm-corner--bl" />
                    <div className="bm-corner bm-corner--br" />

                    {/* Scan line */}
                    {!rotating && <div className="bm-scan-line" />}

                    {/* ── QR Code ──
                        Install: npm install qrcode.react
                        Then uncomment the import at the top.
                        If QRCodeSVG is null (not installed), we show the raw token.
                    ── */}
                    {QRCodeSVG ? (
                      <QRCodeSVG
                        value={token}
                        size={220}
                        bgColor="#FFFFFF"
                        fgColor="#111827"
                        level="M"
                      />
                    ) : (
                      /* Fallback: token display when qrcode.react not installed */
                      <div style={{
                        width: 220, height: 220, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 12,
                        background: '#f9fafb', borderRadius: 8,
                      }}>
                        <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', fontWeight: 600 }}>
                          Install qrcode.react to render QR
                        </div>
                        <div style={{
                          fontSize: 9, fontFamily: 'monospace', color: '#374151',
                          wordBreak: 'break-all', padding: '8px 12px',
                          background: 'white', borderRadius: 8, border: '1px solid #e5e7eb',
                          maxWidth: 200, textAlign: 'center',
                        }}>{token.substring(0, 32)}…</div>
                        <div style={{ fontSize: 10, color: '#9ca3af' }}>npm install qrcode.react</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Info strip */}
            <div style={{
              width: '100%', background: 'rgba(30,58,138,.04)',
              border: '1px solid rgba(30,58,138,.1)', borderRadius: 12,
              padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {[
                { label: 'Valid for', value: '30 seconds · Auto-refreshes' },
                { label: 'Scan mode', value: 'All staff roles' },
                { label: 'GPS verify', value: 'Server-side haversine check' },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{value}</span>
                </div>
              ))}
            </div>

            {/* Instructions */}
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                'Display this on a screen at the school entrance.',
                'Staff open Skoolpillar app → Mark Attendance → scan.',
                'GPS is verified server-side. Out-of-range staff get a location pass QR.',
              ].map((text, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                    background: 'rgba(30,58,138,.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 800, color: '#1e3a8a',
                  }}>{i + 1}</div>
                  <span style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>{text}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AttendanceBarcodeModal;